import { Paper } from "@mui/material";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setQueryOutput,
  setOutputMetadata,
  setComparisonQueryOutput,
  setIsPerformanceApiCalled,
  setOverviewAccountSummaryActiveRequestKey,
  setOverviewAccountSummaryLoadedRequestKey,
  setOverviewAccountSummaryOutput,
  setIsFetchedBudgetData,
  setBudgetDataOfBizDevelopment,
  setBudgetMetaDataOfBizDevelopment,
  setIsBudgetOverused,
} from "../../../../features/bizDevelopmentDashboardSlice";
import BizDevelopmentDashboardSearch from "./BizDevelopmentDashboardSearch";
import BizDevelopmentDashboardTabs from "./BizDevelopmentDashboardTabs";
import { useBizDevelopmentDashboard } from "../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboard";

const BizDevelopmentDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [dataFetched, setDataFetched] = useState(
    () => !!searchParams.get("sites"),
  );
  const {
    fetchDashboardData,
    fetchBudgetData,
    getSitesFromParams,
    getTimeframeFromParams,
    getOverviewAccountSummaryTimeframeFromParams,
    fetchOverviewAccountSummaryData,
  } = useBizDevelopmentDashboard();

  // On refresh, re-trigger API if params are already in the URL
  useEffect(() => {
    const sites = getSitesFromParams();
    if (sites.length === 0) return;
    const activeTab = searchParams.get("tab") || "performance";
    if (activeTab !== "performance") {
      triggerApiForTab(activeTab, sites);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggerApiForTab = (activeTab: string, siteSharedUids: string[]) => {
    switch (activeTab) {
      case "performance":
        fetchDashboardData(getTimeframeFromParams(), siteSharedUids);
        dispatch(setIsPerformanceApiCalled(true));
        break;
      case "overview":
        fetchOverviewAccountSummaryData(
          getOverviewAccountSummaryTimeframeFromParams(),
          siteSharedUids,
        );
        break;
      case "budget_control":
        fetchBudgetData(siteSharedUids);
        break;
      // future cases for other tabs with their own APIs can be added here
      // future tabs will add their own API calls here
      default:
        break;
    }
  };

  const handleGetData = (siteSharedUids: string[]) => {
    setDataFetched(true);
    const activeTab = searchParams.get("tab") || "performance";
    dispatch(setIsPerformanceApiCalled(false));
    dispatch(setIsFetchedBudgetData(false)); // reset budget data fetched state when fetching new data
    triggerApiForTab(activeTab, siteSharedUids);
  };

  const handleClearSites = () => {
    setDataFetched(false);
    setSearchParams(new URLSearchParams());
    dispatch(setQueryOutput([]));
    dispatch(setOutputMetadata({}));
    dispatch(setIsPerformanceApiCalled(false));
    dispatch(setComparisonQueryOutput([]));
    dispatch(setOverviewAccountSummaryOutput([]));
    dispatch(setOverviewAccountSummaryLoadedRequestKey(null));
    dispatch(setOverviewAccountSummaryActiveRequestKey(null));

    //clear budget data in store
    dispatch(setIsFetchedBudgetData(false));
    dispatch(setBudgetDataOfBizDevelopment([]));
    dispatch(setBudgetMetaDataOfBizDevelopment({}));
    dispatch(setIsBudgetOverused(false));
  };

  return (
    <>
      <Paper>
        <BizDevelopmentDashboardSearch
          onGetData={handleGetData}
          onClearSites={handleClearSites}
        />
        {dataFetched && <BizDevelopmentDashboardTabs />}
      </Paper>
    </>
  );
};

export default BizDevelopmentDashboard;
